import React from "react";
import { useTranslation } from "react-i18next";
import "./ViewSwitcher.css";

export type ViewMode = "card" | "table" | "board";

export interface ViewSwitcherProps {
  /** Current view mode */
  viewMode: ViewMode;
  /** Called when view mode changes */
  onViewChange: (mode: ViewMode) => void;
  /** Label for card view button */
  cardLabel?: string;
  /** Label for table view button */
  tableLabel?: string;
  /** Label for board view button */
  boardLabel?: string;
  /** Whether to show the board button */
  showBoard?: boolean;
  /** Additional CSS class name */
  className?: string;
  /** Accessibility label for the switcher group */
  ariaLabel?: string;
}

/**
 * Reusable view mode switcher component for toggling between card, table, and board views.
 */
export const ViewSwitcher: React.FC<ViewSwitcherProps> = ({
  viewMode,
  onViewChange,
  cardLabel,
  tableLabel,
  boardLabel,
  showBoard = false,
  className = "",
  ariaLabel,
}) => {
  const { t } = useTranslation("common");

  return (
    <div
      className={`view-switcher ${className}`}
      role="group"
      aria-label={ariaLabel ?? t("viewSwitcher.ariaLabel")}
    >
      <button
        type="button"
        className={`view-switcher-btn ${viewMode === "card" ? "active" : ""}`}
        onClick={() => onViewChange("card")}
        aria-pressed={viewMode === "card"}
        data-testid="card-view"
      >
        {cardLabel ?? t("viewSwitcher.cardView")}
      </button>
      <button
        type="button"
        className={`view-switcher-btn ${viewMode === "table" ? "active" : ""}`}
        onClick={() => onViewChange("table")}
        aria-pressed={viewMode === "table"}
        data-testid="table-view"
      >
        {tableLabel ?? t("viewSwitcher.tableView")}
      </button>
      {showBoard && (
        <button
          type="button"
          className={`view-switcher-btn ${viewMode === "board" ? "active" : ""}`}
          onClick={() => onViewChange("board")}
          aria-pressed={viewMode === "board"}
          data-testid="board-view"
        >
          {boardLabel ?? t("viewSwitcher.boardView", "Board")}
        </button>
      )}
    </div>
  );
};

export default ViewSwitcher;
